/** Montants (centimes), dates, filtre liste clients, affichage fiche. */
import { COUNTRIES } from "./types";

export { formatCents, formatDay } from "../../formats/display";

export function clientListName(c: { name: string; display_name?: string | null }): string {
  const trade = (c.display_name || "").trim();
  return trade !== "" ? trade : c.name;
}

export function clientSelectOption(c: {
  id: number;
  name: string;
  display_name?: string | null;
  email?: string | null;
  phone?: string | null;
  siret?: string | null;
  contact_name?: string | null;
  contact_names?: string | null;
}): { value: string; label: string; search: string } {
  return {
    value: String(c.id),
    label: clientListName(c),
    search: [c.name, c.display_name, c.email, c.phone, c.siret, c.contact_name, c.contact_names]
      .map((s) => (s || "").trim())
      .filter(Boolean)
      .join(" "),
  };
}

export function clientMatches(
  c: {
    name: string;
    display_name?: string | null;
    email: string | null;
    siret: string | null;
    phone?: string | null;
    contact_name?: string | null;
    contact_names?: string | null;
  },
  raw: string,
): boolean {
  const q = raw.trim().toLowerCase();
  if (q === "") return true;
  const digits = q.replace(/\D/g, "");
  const parts = [c.name, c.display_name, c.email, c.siret, c.phone, c.contact_name, c.contact_names].map((s) =>
    (s || "").toLowerCase(),
  );
  if (parts.some((s) => s.includes(q))) return true;
  if (digits.length >= 3 && (c.siret || "").replace(/\D/g, "").includes(digits)) return true;
  return false;
}

export type ClientListFilter = "all" | "active" | "establishment_closed" | "ceased" | "unchecked" | "archived";

export const CLIENT_FILTERS: { id: ClientListFilter; label: string }[] = [
  { id: "all", label: "Tous" },
  { id: "active", label: "Actifs" },
  { id: "establishment_closed", label: "Établissement fermé" },
  { id: "ceased", label: "Radiés" },
  { id: "unchecked", label: "Non vérifiés" },
  { id: "archived", label: "Archivés" },
];

export function clientListMatch(
  c: { archived?: number | boolean; sirene_status?: string | null },
  filter: ClientListFilter,
): boolean {
  if (filter === "archived") return Boolean(c.archived);
  if (c.archived) return false;
  if (filter === "all") return true;
  if (filter === "unchecked") return !c.sirene_status || c.sirene_status === "unknown";
  return c.sirene_status === filter;
}

export function formatSiret(s: string | null | undefined): string {
  const d = (s || "").replace(/\D/g, "");
  if (d.length !== 14) return s || "—";
  return `${d.slice(0, 3)} ${d.slice(3, 6)} ${d.slice(6, 9)} ${d.slice(9)}`;
}

export function dash(s: string | null | undefined): string {
  const t = (s || "").trim();
  return t === "" ? "—" : t;
}

export function countryLabel(code: string | null | undefined): string {
  const c = COUNTRIES.find((x) => x.code === (code || "").toUpperCase());
  return c?.label || (code || "").trim();
}

/** Solde client : + dû · 0 soldé · − trop-perçu. */
export function formatBalance(cents: number, money: (n: number) => string): { label: string; className: string } {
  if (cents === 0) return { label: "Soldé", className: "fx-clients-table__muted" };
  if (cents > 0) return { label: `${money(cents)} dû`, className: "is-due fx-bal-due" };
  return { label: `${money(-cents)} trop-perçu`, className: "is-credit fx-bal-credit" };
}

export function contactLabel(c: { first_name?: string; last_name?: string } | null | undefined): string {
  return [c?.first_name, c?.last_name].map((s) => (s || "").trim()).filter(Boolean).join(" ");
}

function normLine(s: string): string {
  return s.trim().toLowerCase().replace(/\s+/g, " ");
}

/** Rue + CP/ville. Pas de département, pas France. */
export function formatPostalAddress(parts: {
  line?: string | null;
  line2?: string | null;
  postal_code?: string | null;
  city?: string | null;
  country_code?: string | null;
}): string[] {
  const street = (parts.line || "").trim();
  const extra = (parts.line2 || "").trim();
  const city = [parts.postal_code, parts.city].map((s) => (s || "").trim()).filter(Boolean).join(" ");
  const lines: string[] = [];
  if (street) lines.push(street);
  if (extra && !normLine(street).includes(normLine(extra))) lines.push(extra);
  if (city && !lines.some((l) => normLine(l) === normLine(city))) lines.push(city);
  const cc = (parts.country_code || "FR").toUpperCase();
  if (cc && cc !== "FR") {
    const country = countryLabel(cc);
    if (country && !lines.some((l) => normLine(l) === normLine(country))) lines.push(country);
  }
  return lines;
}
